import { useEffect, useState } from 'react'
import { Avatar, AvatarFallback, cn } from '@axilon-apps/libs-ui-shadcn'
import { getInitials } from '@axilon-apps/libs-shared-utils'

type AvatarUser = { name?: string | null; username?: string | null } | null | undefined

type Palette = { 200: string; 600: string }

// Ten muted hues. A user maps to one hue deterministically (hash of
// name/username) so their colour is stable across the app. A single shade reads
// differently between themes, so we use the pale 200 in light mode and the
// saturated 600 in dark mode.
const HUES: [Palette, ...Palette[]] = [
  { 200: '#e5bcbc', 600: '#7f3f3f' }, // muted red
  { 200: '#e7c7b8', 600: '#7c4632' }, // muted orange
  { 200: '#e6ddb8', 600: '#7a6f2f' }, // muted amber
  { 200: '#c7d9cc', 600: '#3f5f49' }, // muted green
  { 200: '#c7d9d6', 600: '#3f5f59' }, // muted teal
  { 200: '#c7d4de', 600: '#3f556f' }, // muted cyan/blue
  { 200: '#c7cfe6', 600: '#3f517f' }, // muted blue
  { 200: '#d1c7e6', 600: '#51407f' }, // muted indigo
  { 200: '#ddc7e6', 600: '#6a407f' }, // muted violet
  { 200: '#e6c7d6', 600: '#7f405f' } // muted pink
]

function hashString(value: string): number {
  let hash = 0
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0
  }
  return Math.abs(hash)
}

function readIsDark(): boolean {
  if (typeof document === 'undefined') return false
  const root = document.documentElement
  return root.classList.contains('dark') || root.getAttribute('data-theme') === 'dark'
}

// Tracks the app theme via the root element's `.dark` class / `data-theme`
// attribute (set by the theme provider), so UserAvatar can live here without a
// dependency on the dashboard's `useSettings`.
function useIsDarkMode(): boolean {
  const [isDark, setIsDark] = useState(readIsDark)

  useEffect(() => {
    const root = document.documentElement
    const update = () => setIsDark(readIsDark())
    update()
    const observer = new MutationObserver(update)
    observer.observe(root, { attributes: true, attributeFilter: ['class', 'data-theme'] })
    return () => observer.disconnect()
  }, [])

  return isDark
}

interface Props {
  user?: AvatarUser
  // Diameter in pixels (default 32, i.e. Tailwind size-8).
  size?: number
  // Letter shown when the user has no name/username (default 'U').
  fallback?: string
  // Set false to keep the neutral muted background instead of a per-user colour.
  colored?: boolean
  className?: string
}

const UserAvatar = ({ user, size = 32, fallback = 'U', colored = true, className }: Props) => {
  const isDark = useIsDarkMode()

  const initials = getInitials(user, fallback)
  const seed = user?.name || user?.username || initials
  const hue = HUES[hashString(seed) % HUES.length] ?? HUES[0]

  return (
    <Avatar className={cn('shrink-0', className)} style={{ width: size, height: size }}>
      <AvatarFallback
        className="font-semibold"
        style={{
          fontSize: Math.round(size * 0.4),
          ...(colored
            ? { backgroundColor: isDark ? hue[600] : hue[200], color: isDark ? '#fff' : '#111827' }
            : {})
        }}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  )
}

export default UserAvatar
